import React, { useEffect, useRef, useState } from 'react';
import styles from './CustomSelect.module.scss';
import { isNullOrUndefined } from '../../../helpers/object-helpers';
import ChevronDown from '../../Icons/ChevronDown';
import ChevronUp from '../../Icons/ChevronUp';
import { type IOption } from '../../shared/models/IOption';
import DropdownOptionsList, {
  type IDropdownOptionsList,
} from '../../shared/DropdownOptionsList/DropdownOptionsList';

export interface ICustomSelect {
  options: IOption[];
  onChange?: (value: IOption) => void;
  id: string;
  placeholder?: string;
  disabled?: boolean;
  isValid?: boolean;
  defaultOption?: IOption;
  value?: string;
}

export default function CustomSelect({
  options,
  onChange,
  id,
  placeholder,
  disabled = false,
  isValid,
  defaultOption,
  value,
}: ICustomSelect) {
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);

  const [selectedOption, setSelectedOption] = useState<IOption | null | undefined>(
    !isNullOrUndefined(defaultOption)
      ? defaultOption
      : !isNullOrUndefined(placeholder)
        ? null
        : options[0],
  );

  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!isNullOrUndefined(selectedOption) && !isNullOrUndefined(value)) {
      if (value !== '' && value !== selectedOption.value)
        setSelectedOption(options.find((option) => option.value === value));
      if (value === '') setSelectedOption(null);
    }
  }, [value]);

  const initialValue = placeholder ?? selectedOption?.text;

  function isOptionSelected() {
    return selectedOption !== null;
  }

  function onOptionClick(option: IOption) {
    setSelectedOption(option);
    setDropdownOpen(!dropdownOpen);

    if (onChange !== undefined) onChange(option);
  }

  function toggleDropdown() {
    setDropdownOpen((prev) => !prev);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (dropdownOpen || event.key !== 'ArrowDown') return;

    event.preventDefault();
    setDropdownOpen(true);
    setTimeout(() => {
      (listRef.current?.childNodes[0] as HTMLElement).focus();
    }, 0);
  }

  const dropdownValidationClass = !isNullOrUndefined(isValid)
    ? isValid
      ? styles.valid
      : styles.invalid
    : '';

  const dropdownOptionsListProps: IDropdownOptionsList = {
    id,
    options,
    selectedOption,
    onOptionClick,
    listRef,
    dropdownOpen,
  };

  return (
    <div className={styles.customSelect}>
      <button
        id={`${id}-button`}
        aria-haspopup="listbox"
        role="combobox"
        type="button"
        disabled={disabled}
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        aria-controls={`${id}-list`}
        aria-expanded={dropdownOpen}
        aria-labelledby={`${id}-label`}
        aria-activedescendant={isOptionSelected() ? `${id}-option-${selectedOption?.value}` : ''}
        className={dropdownValidationClass}
      >
        <span
          className={
            disabled
              ? styles.disabledOptionText
              : !isOptionSelected() && placeholder != null
                ? styles.placeholderText
                : ''
          }
        >
          {selectedOption?.text ?? initialValue}
        </span>
        {dropdownOpen ? <ChevronUp /> : <ChevronDown />}
      </button>
      <DropdownOptionsList {...dropdownOptionsListProps} />
    </div>
  );
}
